import { useMemo, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0DatePicker, F0Dialog } from '@factorialco/f0-react'
import type { DatePickerValue } from '@factorialco/f0-react/dist/experimental'
import { Form } from 'factorial-form'
import {
  BulkCreateWorkSchedulePlanningVersionsDocument,
  EmployeesEmployeeSelectorInput,
  WorkScheduleEmployeesDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useMutation } from 'modules/core/hooks/useMutation'
import { dateUIToBackend, i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import type WorkSchedule from 'modules/work_schedules/store/models/Schedule'

interface AddEmployeesModalProps {
  workSchedule: WorkSchedule
  onClose: () => void
  onSuccess: () => void
}

const dateToDatePickerValue = (date: Date): DatePickerValue => {
  return {
    granularity: 'day',
    value: {
      from: date,
      to: date,
    },
  }
}

const AddEmployeesModal = ({ workSchedule, onClose, onSuccess }: AddEmployeesModalProps) => {
  const [selectedEmployees, setSelectedEmployees] = useState<EmployeesEmployeeSelectorInput>({
    type: 'manual',
    ids: [],
  })

  // get employees already assigned to this work schedule

  const form = useLazyRef<Form>(
    () =>
      new Form(
        {
          effective_at: i18nDateToString(moment(), 'backend'),
        },
        {
          effective_at: 'date',
        }
      )
  ).current

  const [bulkCreateWorkSchedulePlanningVersions, { loading: _loading }] = useMutation(
    BulkCreateWorkSchedulePlanningVersionsDocument,
    {
      refetchQueries: [WorkScheduleEmployeesDocument],
      extractorFn: (data) => data.timePlanningMutations.bulkCreateTimeplanningPlanningVersion,
    }
  )

  const effectiveAtValue = form.get('effective_at')?.value

  const datePickerValue = useMemo((): DatePickerValue => {
    if (!effectiveAtValue) {
      return dateToDatePickerValue(new Date())
    }
    const parsedDate = stringToDate(effectiveAtValue, 'backend')
    if (!parsedDate.isValid()) {
      return dateToDatePickerValue(new Date())
    }
    return dateToDatePickerValue(parsedDate.toDate())
  }, [effectiveAtValue])

  const onSubmit = async () => {
    const response = await bulkCreateWorkSchedulePlanningVersions({
      variables: {
        effectiveAt: dateUIToBackend(form.get('effective_at').value),
        scheduleId: workSchedule.attributes.id,
        employeeSelection: selectedEmployees,
      },
    })
    const handled = handleResult(response, () => undefined)
    if (handled.success) {
      onSuccess()
      onClose()
    }
  }

  return (
    <F0Dialog
      isOpen
      width="sm"
      onClose={() => {
        if (_loading) return
        onClose()
      }}
      title={i18n.t('settings.work_schedule.employees_tab.employee_modal.assign')}
      description={i18n.t('settings.work_schedule.employees_tab.employee_modal.description')}
      primaryAction={{
        label: i18n.t('settings.work_schedule.employees_tab.employee_modal.assign'),
        onClick: onSubmit,
        disabled: _loading,
        loading: _loading,
      }}
      secondaryAction={{
        label: i18n.t('settings.work_schedule.cancel'),
        onClick: onClose,
      }}
    >
      <>
        <EmployeeSelectorV2
          messages={{
            placeholder: i18n.t('common.select_users'),
          }}
          forcedFilters={{
            excludeIds: workSchedule.employeeIds,
          }}
          onChange={setSelectedEmployees}
          multiple
          options={{
            hideSearchIcon: true,
          }}
        />

        <Box paddingY="s16">
          <F0DatePicker
            label={i18n.t('settings.work_schedule.employees_tab.table.header.date')}
            onChange={(date) => {
              if (!date?.value?.from) return
              form.get('effective_at').set(date.value.from)
            }}
            size="md"
            value={datePickerValue}
            hint={i18n.t('settings.work_schedule.employees_tab.employee_modal.footer_description')}
          />
        </Box>
      </>
    </F0Dialog>
  )
}

export default AddEmployeesModal
